/**
 * useAISettings Hook - Global AI Assistant Settings Management
 * 
 * Provides centralized management for AI assistant visibility and settings
 * across the application. Handles localStorage persistence for user preferences.
 * 
 * Features:
 * - Global AI assistant enable/disable toggle
 * - Persistent settings via localStorage
 * - Consistent API across components
 * - Default enabled state for new users
 * 
 * Usage:
 * ```javascript
 * const { aiEnabled, toggleAI, setAiEnabled } = useAISettings();
 * ```
 */

import { useState, useEffect } from 'react';

const STORAGE_KEY = 'zavvyAiEnabled';
const DEFAULT_AI_ENABLED = true;

/**
 * Custom hook for managing AI assistant settings globally
 * @returns {Object} AI settings state and controls
 */
const useAISettings = () => {
  const [aiEnabled, setAiEnabledState] = useState(DEFAULT_AI_ENABLED);
  const [isLoading, setIsLoading] = useState(true);

  // Load AI setting from localStorage on mount
  useEffect(() => {
    try {
      const storedSetting = localStorage.getItem(STORAGE_KEY);
      if (storedSetting !== null) {
        setAiEnabledState(JSON.parse(storedSetting));
      }
    } catch (error) {
      console.warn('Failed to load AI settings from localStorage:', error);
      // Keep default value on error
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Update AI enabled state and persist to localStorage
   * @param {boolean} enabled - New AI enabled state
   */
  const setAiEnabled = (enabled) => {
    try {
      setAiEnabledState(enabled);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(enabled));
      
      // Log setting change for analytics
      console.log(`Zavvy AI Assistant ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Failed to save AI settings to localStorage:', error);
      // Still update state even if localStorage fails
      setAiEnabledState(enabled);
    }
  };

  /**
   * Toggle AI enabled state
   */
  const toggleAI = () => {
    setAiEnabled(!aiEnabled);
  };

  /**
   * Reset AI settings to default
   */
  const resetAISettings = () => {
    setAiEnabled(DEFAULT_AI_ENABLED);
  };

  /**
   * Get current AI settings for debugging/analytics
   */
  const getAISettings = () => ({
    aiEnabled,
    storageKey: STORAGE_KEY,
    defaultValue: DEFAULT_AI_ENABLED,
    isLoading
  });

  return {
    // Primary API
    aiEnabled,
    setAiEnabled,
    toggleAI,
    isLoading,
    
    // Utility functions
    resetAISettings,
    getAISettings
  };
};

export default useAISettings;